import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} VariantPriceSyncConfig
 * @property {string} sectionId
 * @property {string} moneyFormat
 * @property {string} currency
 */

class DelphiVariantPriceSync {
  /** @type {HTMLElement} */
  #root;

  /** @type {VariantPriceSyncConfig} */
  #config;

  /**
   * @param {HTMLElement} root
   * @param {VariantPriceSyncConfig} config
   */
  constructor(root, config) {
    this.#root = root;
    this.#config = config;
    this.#root.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    if (!('detail' in event) || !event.detail || typeof event.detail !== 'object') return;

    const form = this.#root.querySelector('product-form-component');
    const productId = 'data' in event.detail && event.detail.data ? event.detail.data.productId : undefined;

    if (form && productId && form.dataset.productId !== productId) return;

    const resource = 'resource' in event.detail ? event.detail.resource : null;
    if (!resource || typeof resource.price !== 'number') return;

    const priceEl = this.#root.querySelector('[data-cdp-price]');
    const stickyPriceEl = this.#root.querySelector('[data-cdp-sticky-price]');
    const sizeEl = this.#root.querySelector('[data-cdp-size]');
    const formatted = formatMoney(resource.price, this.#config.moneyFormat, this.#config.currency);

    if (priceEl) {
      priceEl.dataset.basePriceCents = String(resource.price);
      priceEl.textContent = formatted;
    }

    if (stickyPriceEl) {
      stickyPriceEl.dataset.basePriceCents = String(resource.price);
      stickyPriceEl.textContent = formatted;
    }

    const variantTitle = resource.public_title || resource.title;
    if (sizeEl && variantTitle) sizeEl.textContent = variantTitle;
  };
}

function initVariantPriceSync() {
  document.querySelectorAll('script[data-cdp-variant-price-config]').forEach((script) => {
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return;

    /** @type {VariantPriceSyncConfig} */
    let config;
    try {
      config = JSON.parse(script.textContent);
    } catch {
      return;
    }

    const root = document.getElementById(`shopify-section-${config.sectionId}`);
    if (!root) return;

    new DelphiVariantPriceSync(root, config);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVariantPriceSync);
} else {
  initVariantPriceSync();
}
